import React from "react";

/**
 * Lightweight WebRTC call-health / call-quality indicator.
 * Consumes the `health` object exposed by useAnonymousCall().
 *
 * Shown while a call is connecting or connected, on BOTH scanner and owner
 * sides. Zero PII — only transport + audio-track diagnostics.
 */
const Dot = ({ ok, warn }) => (
  <span
    className={`inline-block w-2 h-2 rounded-full mr-1.5 ${
      ok ? "bg-emerald-400" : warn ? "bg-amber-400" : "bg-slate-500"
    }`}
  />
);

const Row = ({ label, value, testid }) => (
  <div className="flex justify-between text-[11px] font-mono py-0.5" data-testid={testid}>
    <span className="text-slate-400">{label}</span>
    <span className="text-slate-100 text-right">{value}</span>
  </div>
);

const CallHealthPanel = ({ health, role = "scanner" }) => {
  // iter61 Fase 3: diagnostics are for field debugging only — end users
  // never see this panel unless the URL carries ?debug=1.
  let debugEnabled = false;
  try { debugEnabled = new URLSearchParams(window.location.search).get("debug") === "1"; } catch (_) { /* noop */ }
  if (!debugEnabled || !health) return null;
  const h = health;
  const iceOk = h.iceState === "connected" || h.iceState === "completed";
  const connOk = h.connectionState === "connected";
  const hasAudio = h.remoteAudioReceived && h.localAudioLive;
  const testidPrefix = role === "owner" ? "owner-health" : "call-health";

  return (
    <div
      className="mt-3 rounded-lg border border-white/10 bg-black/25 px-3 py-2"
      data-testid={testidPrefix}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-slate-400">
          Diagnostico de llamada
        </span>
        <span className="text-[10px] text-slate-500" data-testid={`${testidPrefix}-source`}>
          {h.iceSource === "cloudflare" ? "TURN: Cloudflare" : h.iceSource === "open_relay" ? "TURN: Open Relay (respaldo)" : h.iceSource === "twilio" ? "TURN: Twilio" : h.iceSource === "stun_fallback" ? "Solo STUN" : "—"}
        </span>
      </div>

      <Row
        label="Conexion"
        value={
          <span className="inline-flex items-center" data-testid={`${testidPrefix}-connection`}>
            <Dot ok={connOk} warn={h.connectionState === "connecting"} />
            {h.connectionState || "—"}
          </span>
        }
      />
      <Row
        label="ICE"
        value={
          <span className="inline-flex items-center" data-testid={`${testidPrefix}-ice`}>
            <Dot ok={iceOk} warn={h.iceState === "checking"} />
            {h.iceState || "—"}
          </span>
        }
      />
      <Row
        label="Candidato"
        value={
          <span data-testid={`${testidPrefix}-candidate`}>
            {h.candidateType || "—"} {h.turnUsed && <span className="text-violet-300">(TURN)</span>}
          </span>
        }
      />
      <Row
        label="TURN relay"
        value={
          <span data-testid={`${testidPrefix}-turn-used`}>
            {h.turnUsed ? "si" : h.iceState ? "no" : "—"}
          </span>
        }
      />
      <Row
        label="Audio local"
        value={
          <span className="inline-flex items-center" data-testid={`${testidPrefix}-local-audio`}>
            <Dot ok={h.localAudioLive} />
            {h.localAudioLive ? "activo" : "—"}
          </span>
        }
      />
      <Row
        label="Audio remoto"
        value={
          <span className="inline-flex items-center" data-testid={`${testidPrefix}-remote-audio`}>
            <Dot ok={h.remoteAudioReceived} />
            {h.remoteAudioReceived ? "recibido" : "—"}
          </span>
        }
      />
      {h.rttMs != null && (
        <Row label="RTT" value={<span data-testid={`${testidPrefix}-rtt`}>{h.rttMs} ms</span>} />
      )}
      {h.jitterMs != null && (
        <Row label="Jitter" value={<span data-testid={`${testidPrefix}-jitter`}>{h.jitterMs} ms</span>} />
      )}
      {h.packetsLost != null && (
        <Row
          label="Paquetes perdidos"
          value={<span data-testid={`${testidPrefix}-packets-lost`}>{h.packetsLost}</span>}
        />
      )}
      {h.bitrateKbps != null && (
        <Row
          label="Bitrate"
          value={<span data-testid={`${testidPrefix}-bitrate`}>{h.bitrateKbps} kbps</span>}
        />
      )}

      {!hasAudio && connOk && (
        <div className="mt-2 text-[11px] text-amber-300" data-testid={`${testidPrefix}-silent-warning`}>
          Conectado pero sin audio confirmado. Verifica el microfono o espera unos segundos.
        </div>
      )}
    </div>
  );
};

export default CallHealthPanel;
